import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { PortalDsAlert } from 'src/components/portal-design-system/portal-ds-alert';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import Checkbox from 'src/components/form-new/checkbox';
import { Client, ClientStudioSyncResponse } from '../../types/client.types';
import { useSyncClientToStudioMutation } from '../../endpoints/client.endpoints';
import { useDispatch } from 'react-redux';
import notificationService from 'src/services/notification/notification.service';

import { STUDIO_MIGRATION_WEBSITE_REQUIRED_MESSAGE } from '../../utils/client-url.utils';

interface MigrateClientModalProps {
  open: boolean;
  client: Client | null;
  onOpenChange: (open: boolean) => void;
  onMigrated: (result: ClientStudioSyncResponse) => void;
}

const provisionableUserCount = (client: Client): number =>
  (client.users ?? []).filter((user) => Boolean(user.email?.trim())).length;

const extractSyncError = (error: unknown): string => {
  if (!error || typeof error !== 'object') {
    return 'Failed to migrate client to Studio';
  }

  const record = error as {
    responseData?: { error?: unknown; error_code?: unknown; detail?: unknown };
    data?: { error?: unknown; error_code?: unknown; detail?: unknown };
    message?: unknown;
  };

  const payload = record.responseData ?? record.data;
  if (payload && typeof payload === 'object') {
    if (payload.error_code === 'website_required' && typeof payload.detail === 'string') {
      return payload.detail;
    }
  }

  const fromResponse = record.responseData?.error;
  if (typeof fromResponse === 'string' && fromResponse.trim()) {
    return fromResponse;
  }

  const fromData = record.data?.error;
  if (typeof fromData === 'string' && fromData.trim()) {
    return fromData;
  }

  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message;
  }

  return 'Failed to migrate client to Studio';
};

export const MigrateClientModal: React.FC<MigrateClientModalProps> = ({
  open,
  client,
  onOpenChange,
  onMigrated,
}) => {
  const dispatch = useDispatch();
  const [reconcile, setReconcile] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [syncClientToStudio, { isLoading }] = useSyncClientToStudioMutation();

  useEffect(() => {
    if (!open) {
      setReconcile(false);
      setInlineError(null);
    }
  }, [open]);

  if (!client) {
    return null;
  }

  const userCount = provisionableUserCount(client);
  const isResync = Boolean(client.studio_migrated_at);

  const handleConfirm = async() => {
    setInlineError(null);
    try {
      const result = await syncClientToStudio({
        clientId: client.ext_client_id,
        body: { invite: false, reconcile },
      }).unwrap();
      onOpenChange(false);
      onMigrated(result);
      notificationService.handleSuccess(
        result,
        dispatch,
        isResync
          ? `${client.title} re-synced to Studio`
          : `${client.title} migrated to Studio`
      );
    } catch (error) {
      const message = extractSyncError(error);
      setInlineError(message);
      notificationService.handleError(error, dispatch, message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="sm:max-w-md bg-white"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className={portalDs.sectionTitle}>
          {isResync ? 'Re-sync to Studio' : 'Migrate to Studio'}
        </DialogTitle>
        <p className={portalDs.sheetDescription}>
          This will create a Studio team, workspace and brand for
          {' '}
          <strong>
            {client.title}
          </strong>
          , and provision Studio accounts for
          {' '}
          {userCount}
          {' '}
          client user
          {userCount === 1 ? '' : 's'}
          {' '}
          with an email address.
        </p>

        <div className="flex flex-col gap-3 mt-2">
          <div className="flex flex-col gap-1">
            <Checkbox
              value={reconcile}
              onChange={setReconcile}
              label="Remove Studio access for users no longer on this client"
              disabled={isLoading}
            />
            <p className={portalDs.hint}>
              Off by default so migration is additive only.
            </p>
          </div>
        </div>

        {inlineError ? (
          <PortalDsAlert
            variant="danger"
            title="Migration failed"
            className="mt-3"
          >
            {inlineError}
          </PortalDsAlert>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <PortalDsButton
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </PortalDsButton>
          <PortalDsButton
            variant="primary"
            size="sm"
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isLoading}
          >
            {isLoading ? 'Migrating…' : 'Migrate to Studio'}
          </PortalDsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MigrateClientModal;
